// src/runtime/KnowledgeRuntime/types.ts
// ============================================
// Knowledge Runtime - 完整类型定义
// 所有 Knowledge 相关类型集中管理
// 依赖 shared.ts 中的 ID、Timestamp 保持一致
// ============================================

import type { ID, Timestamp } from '../../types/shared';

// ============================================
// 基础类型 - 复用 shared
// ============================================
export type KnowledgeID = ID;
export type KnowledgeTimestamp = Timestamp;

// ============================================
// KnowledgeNote - 笔记核心模型
// ============================================
export interface KnowledgeNote {
  id: KnowledgeID;
  title: string;
  fileName?: string; // 可选，Obsidian 文件名
  content: string;
  summary?: string; // 搜索列表展示用
  tags: string[];
  folderId?: string;
  folderName?: string;
  createdAt: KnowledgeTimestamp;
  updatedAt: KnowledgeTimestamp;
  metadata?: Record<string, unknown>;
}

// ============================================
// KnowledgeGraph - 图谱
// ============================================
export interface KnowledgeNode {
  id: KnowledgeID;
  label: string;
  type: 'note' | 'folder' | 'tag' | 'daily' | 'canvas' | 'image' | 'pdf' | 'other';
  metadata?: Record<string, unknown>;
}

export interface KnowledgeEdge {
  source: KnowledgeID;
  target: KnowledgeID;
  relation: 'link' | 'reference' | 'embed' | 'mention' | 'parent' | 'child' | 'tag';
  metadata?: Record<string, unknown>;
}

export interface KnowledgeGraph {
  nodes: KnowledgeNode[];
  edges: KnowledgeEdge[];
}

// ============================================
// KnowledgeRecent - 最近访问
// ============================================
export interface KnowledgeRecentItem {
  id: KnowledgeID;
  title: string;
  fileName?: string;
  accessedAt: KnowledgeTimestamp;
}

// ============================================
// KnowledgeDaily - 每日笔记
// ============================================
export interface KnowledgeDaily {
  date: string; // YYYY-MM-DD
  notes: KnowledgeNote[];
}

// ============================================
// KnowledgeFolder - 文件夹
// ============================================
export interface KnowledgeFolder {
  id: string;
  name: string;
  path: string;
  parentId?: string;
  noteCount: number;
}

// ============================================
// KnowledgeTag - 标签
// ============================================
export interface KnowledgeTag {
  name: string;
  count: number;
}

// ============================================
// KnowledgeSearchOptions - 搜索选项
// ============================================
export interface KnowledgeSearchOptions {
  keyword?: string;
  tag?: string;
  folder?: string;
  limit?: number;
  offset?: number;
  sort?: 'relevance' | 'date' | 'title';
  date?: string;
  fullText?: boolean;
}

// ============================================
// KnowledgeSearchResult - 搜索结果
// ============================================
export interface KnowledgeSearchResult {
  note: KnowledgeNote;
  score?: number;
  highlights?: {
    field: string;
    snippet: string;
  }[];
}

// ============================================
// KnowledgeQuery - 查询类型枚举
// ============================================
export enum KnowledgeQuery {
  Search = 'search',
  Recent = 'recent',
  Graph = 'graph',
  Tags = 'tags',
  Folders = 'folders',
  Daily = 'daily',
  Note = 'note',
  Backlinks = 'backlinks',
  Outgoing = 'outgoing',
  // === P0-5 Obsidian AI Memory ===
  /** 自然语言知识查询（LLM 提取关键词 + 搜索 + 生成答案） */
  Ask = 'ask',
  /** LLM 增强知识图谱（语义边、聚类、摘要） */
  EnhanceGraph = 'enhance_graph',
  /** Memory↔Knowledge 联想查询（同时检索笔记和记忆） */
  Recall = 'recall',
  // === P2-7 Obsidian 知识库优化 ===
  /** 深度推理查询（多次 LLM 调用 + 推理链 + 追问） */
  AskWithReasoning = 'ask_with_reasoning',
  /** 增强图谱 + 详细聚类信息 */
  EnhanceGraphWithClustering = 'enhance_graph_with_clustering',
  /** 追问查询（基于之前问答上下文） */
  FollowUpAsk = 'follow_up_ask',
  // === Sprint 1 Task 1-3：CRM / SKU / SOP 数据层 ===
  /** 列出所有客户档案 */
  CRMList = 'crm_list',
  /** 获取单个客户档案 */
  CRMGet = 'crm_get',
  /** 找出 N 天未联系的客户 */
  CRMFindStale = 'crm_find_stale',
  /** 找出快成交客户（status=negotiating） */
  CRMFindClosing = 'crm_find_closing',
  /** 找出应发案例的客户（prospecting 且无成交） */
  CRMFindNeedingCase = 'crm_find_needing_case',
  /** 列出所有 SKU */
  SKUList = 'sku_list',
  /** 获取单个 SKU */
  SKUGet = 'sku_get',
  /** 搜索 SKU */
  SKUSearch = 'sku_search',
  /** 列出所有 SOP */
  SOPList = 'sop_list',
  /** 获取单个 SOP */
  SOPGet = 'sop_get',
  /** 搜索 SOP */
  SOPSearch = 'sop_search',
  // === Sprint 4 自动流转：VaultBridge 集成 ===
  /** RAG 检索知识卡片（09_Knowledge/Cards/） */
  SearchCards = 'search_cards',
  /** 列出所有知识卡片（可选按 category 过滤） */
  ListCards = 'list_cards',
  /** 列出灵感条目（可选按子目录过滤） */
  ListIdeas = 'list_ideas',
  /** 列出待升级的 Ideas（rating >= 4 且 status = new/reviewed） */
  ListIdeasForPromotion = 'list_ideas_for_promotion',
  /** 列出研究报告（09_Knowledge/Research/） */
  ListResearch = 'list_research',
  /** 列出 Vault 根目录下所有 .md 文件（不限于 09_Knowledge/Cards，覆盖整个 Obsidian Vault） */
  ListAllNotes = 'list_all_notes',
  // === Program A Phase A：企业知识资产视角 ===
  /** 知识资产统计（各分类数量 + 每日增长 + 资产总览） */
  AssetStats = 'asset_stats',
  // === Program A Phase A+：企业资产实体视角（资产作为一等公民，可被 Runtime 调用） ===
  /** 资产目录：返回所有资产实体（含路径/标题/分类/标签/价值/复用次数） */
  AssetCatalog = 'asset_catalog',
  /** 资产检索：跨所有资产分类按关键词检索 */
  AssetSearch = 'asset_search',
  /** 单个资产详情：返回资产元数据 + 关联产品 + 被谁调用过 */
  AssetDetail = 'asset_detail',
}

// ============================================
// KnowledgeCommand - 命令类型枚举
// ============================================
export enum KnowledgeCommand {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
  Rename = 'rename',
  Move = 'move',
  Append = 'append',
  Open = 'open',
  Sync = 'sync',
  // === P0-5 Obsidian AI Memory ===
  /** AI 记忆命令（通过 params.action 区分子操作：reflect / record_access / auto_tag） */
  AIMemory = 'ai_memory',
  // === P2-7 Obsidian 知识库优化 ===
  /** 自动整理 Vault（补充 backlinks/tags/summary） */
  AutoOrganize = 'auto_organize',
  // === Sprint 1 Task 1-3：CRM / SKU / SOP 写入命令 ===
  /** 创建客户档案 */
  CRMCreate = 'crm_create',
  /** 更新客户档案 */
  CRMUpdate = 'crm_update',
  /** 创建 SKU */
  SKUCreate = 'sku_create',
  /** 更新 SKU */
  SKUUpdate = 'sku_update',
  /** 创建 SOP */
  SOPCreate = 'sop_create',
  /** 更新 SOP */
  SOPUpdate = 'sop_update',
  // === Sprint 4 自动流转：VaultBridge 集成 ===
  /** Idea → Card 升级（params: ideaId, targetCategory） */
  PromoteIdeaToCard = 'promote_idea_to_card',
  /** Card → Domain 升级（params: cardId, domain） */
  PromoteCardToDomain = 'promote_card_to_domain',
  /** 写入 Outputs 目录（params: platform, title, content, status） */
  WriteOutput = 'write_output',
  /** 写入 Reviews 目录（params: type, date, content） */
  WriteReview = 'write_review',
  /** 写入 Ideas 目录（params: subDir, title, content, source, url） */
  WriteIdea = 'write_idea',
  /** 批量执行 Memory Consolidation（Idea→Card→Domain 全流程） */
  ConsolidateVault = 'consolidate_vault',
}

// ============================================
// KnowledgeQueryParams - 查询参数
// ============================================
export interface KnowledgeQueryParams {
  type: KnowledgeQuery;
  options?: KnowledgeSearchOptions;
  id?: KnowledgeID;
  date?: string;
}

// ============================================
// KnowledgeCommandParams - 命令参数
// ============================================
export interface KnowledgeCommandParams {
  id?: KnowledgeID;
  title?: string;
  fileName?: string;
  content?: string;
  newTitle?: string;
  targetFolder?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

// ============================================
// KnowledgeResult - 统一返回值
// data 为可选，与 success/error 语义一致
// ============================================
export interface KnowledgeResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: KnowledgeTimestamp;
}

// ============================================
// KnowledgeAssetStats - 企业知识资产统计（Program A Phase A）
// ============================================
// 用于 KnowledgeWorkspace 顶部资产面板展示
// 数据来源：扫描 09_Knowledge 下各子目录的文件数量 + 按日期统计增量
export interface KnowledgeAssetCategory {
  /** 资产分类标识（cards / ideas / research / domain / prompts / workflows / sops / reviews / outputs） */
  key: string;
  /** 中文显示名 */
  label: string;
  /** 当前总数量 */
  count: number;
  /** 每日增长目标（如 Ideas 10条/天） */
  dailyTarget: number;
  /** 今日新增数量 */
  todayAdded: number;
  /** 本周新增数量 */
  weekAdded: number;
  /** Vault 子目录路径 */
  vaultPath: string;
}

export interface KnowledgeAssetStats {
  /** 资产总数量 */
  totalAssets: number;
  /** 今日新增总数 */
  todayAdded: number;
  /** 本周新增总数 */
  weekAdded: number;
  /** 各分类资产统计 */
  categories: KnowledgeAssetCategory[];
  /** 最近 7 天每日新增趋势 [{date, count}] */
  dailyGrowth: Array<{ date: string; count: number }>;
  /** 知识资产完整度（0-100，基于每日目标达成率） */
  healthScore: number;
  /** 统计时间 */
  statsAt: string;
}

// ============================================
// KnowledgeAsset - 企业知识资产实体（Program A Phase A+）
// ============================================
// 核心理念：知识不是文件，是资产。每个资产可以被 Runtime 调用、被产品引用、被内容复用。
// 所有 Runtime 第一步都是 Knowledge Search → 找到 Asset → 调用 → 沉淀回 Asset
export interface KnowledgeAsset {
  /** 资产 ID（基于路径哈希） */
  id: string;
  /** 资产标题（取自文件名首行或 frontmatter title） */
  title: string;
  /** 资产分类 key（cards / ideas / research / domain / prompts / workflows / sops / reviews / outputs） */
  category: string;
  /** 资产中文分类名 */
  categoryLabel: string;
  /** Vault 相对路径 */
  path: string;
  /** 文件名 */
  fileName: string;
  /** 内容摘要（前 200 字符） */
  summary: string;
  /** 提取的标签（来自 frontmatter 或正文 #tag） */
  tags: string[];
  /** 创建时间（取自 frontmatter 或文件 mtime） */
  createdAt: string;
  /** 最后修改时间 */
  updatedAt: string;
  /** 资产价值评分（0-100，基于复用次数 + 引用数 + 时效性） */
  valueScore: number;
  /** 被引用次数（其他资产 / 产品 / 内容引用此资产的次数） */
  reuseCount: number;
  /** 关联产品 ID 列表（如某 Card 被 Hotel Runtime 引用） */
  relatedProducts: string[];
  /** 最近被哪个 Runtime 调用过 */
  lastUsedBy?: string;
  /** 最近被调用时间 */
  lastUsedAt?: string;
  /** 资产生命周期阶段（draft → active → mature → legacy → archive） */
  lifecycle: 'draft' | 'active' | 'mature' | 'legacy' | 'archive';
}

/** 资产目录（按分类分组） */
export interface KnowledgeAssetCatalog {
  /** 目录生成时间 */
  catalogAt: string;
  /** 资产总数 */
  totalAssets: number;
  /** 按分类分组的资产 */
  categories: Array<{
    key: string;
    label: string;
    dailyTarget: number;
    assets: KnowledgeAsset[];
  }>;
  /** 高价值资产（valueScore >= 70） */
  highValueAssets: KnowledgeAsset[];
  /** 最近被调用的资产（lastUsedAt 排序，前 10） */
  recentlyUsed: KnowledgeAsset[];
  /** 待激活资产（lifecycle = draft 且 createdAt 在 7 天内） */
  draftAssets: KnowledgeAsset[];
}

/** 资产检索选项 */
export interface KnowledgeAssetSearchOptions {
  /** 关键词（标题 / 摘要 / 标签 全文匹配） */
  keyword?: string;
  /** 分类过滤 */
  category?: string;
  /** 标签过滤 */
  tag?: string;
  /** 关联产品过滤 */
  productId?: string;
  /** 最低价值评分 */
  minValueScore?: number;
  /** 生命周期阶段过滤 */
  lifecycle?: KnowledgeAsset['lifecycle'];
  /** 返回数量上限（默认 50） */
  limit?: number;
  /** 排序：value（价值）/ recent（时间）/ reuse（复用次数） */
  sort?: 'value' | 'recent' | 'reuse';
}

/** 资产检索结果 */
export interface KnowledgeAssetSearchResult {
  query: KnowledgeAssetSearchOptions;
  total: number;
  assets: KnowledgeAsset[];
  searchedAt: string;
}

/** 资产详情（含调用历史与依赖图） */
export interface KnowledgeAssetDetail extends KnowledgeAsset {
  /** 内容全文（截断到 2000 字符） */
  content: string;
  /** 调用历史（最近 10 次） */
  usageHistory: Array<{
    runtime: string;
    usedAt: string;
    purpose?: string;
  }>;
  /** 依赖的资产（前置资产） */
  dependsOn: KnowledgeAsset[];
  /** 被哪些资产依赖（后置资产） */
  dependedBy: KnowledgeAsset[];
}